// M08 二次函數與二次方程 - 主題 11-20 數據文件
// Quadratic Functions and Quadratic Equations - Topics 11-20
// 系統化設計，準備後續 Supabase 數據遷移

// M08-11: 建模 (Mathematical Modeling)
export const M08_TOPICS_11_TO_20 = [
  {
    id: 'M08_11',
    name: '建模',
    description: '運用二次函數建立數學模型解決實際問題',
    category: 'mathematical_modeling',
    difficulty: 'advanced'
  },
  {
    id: 'M08_12',
    name: '待定係數',
    description: '利用待定係數法確定二次函數解析式',
    category: 'undetermined_coefficients',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_13',
    name: '重根條件',
    description: '掌握二次方程重根的判定與應用',
    category: 'repeated_roots',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_14',
    name: '根式',
    description: '處理含根式的二次方程與函數',
    category: 'radical_expressions',
    difficulty: 'advanced'
  },
  {
    id: 'M08_15',
    name: '根的分析',
    description: '深入分析二次方程根的性質與關係',
    category: 'root_analysis',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_16',
    name: '根與係數',
    description: '掌握韋達定理及其應用',
    category: 'vieta_theorem',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_17',
    name: '特徵點標註',
    description: '識別和標註二次函數的關鍵特徵點',
    category: 'characteristic_points',
    difficulty: 'basic'
  },
  {
    id: 'M08_18',
    name: '配方法',
    description: '熟練運用配方法處理二次函數',
    category: 'completing_square',
    difficulty: 'basic'
  },
  {
    id: 'M08_19',
    name: '區間評估',
    description: '在指定區間內評估二次函數的性質',
    category: 'interval_analysis',
    difficulty: 'intermediate'
  },
  {
    id: 'M08_20',
    name: '頂點公式',
    description: '運用頂點公式分析二次函數',
    category: 'vertex_formula',
    difficulty: 'basic'
  }
];

// M08 問題數據 - 主題 11-20
export const M08_PROBLEMS_11_TO_20 = [
  // M08-11: 建模
  {
    id: 'M08_11_01',
    topicId: 'M08_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '利潤最大化問題：',
    parts: {
      a: {
        question: '某工廠生產產品，每件成本 $20$ 元，售價為 $x$ 元。市場調研顯示，當售價為 $x$ 元時，月銷量為 $(100-x)$ 件。建立月利潤函數',
        solution: {
          steps: [
            '月利潤 = 每件利潤 × 月銷量',
            '每件利潤 = 售價 - 成本 = $x - 20$ 元',
            '月銷量 = $100 - x$ 件',
            '月利潤函數：$P(x) = (x-20)(100-x) = -x^2 + 120x - 2000$'
          ],
          answer: '$P(x) = -x^2 + 120x - 2000$'
        },
        hint: '利潤 = (售價 - 成本) × 銷量'
      },
      b: {
        question: '求使月利潤最大的售價',
        solution: {
          steps: [
            '利潤函數 $P(x) = -x^2 + 120x - 2000$ 是開口向下的拋物線',
            '頂點橫坐標：$x = -\\frac{b}{2a} = -\\frac{120}{2(-1)} = 60$',
            '當售價為 $60$ 元時，月利潤最大',
            '最大月利潤：$P(60) = -(60)^2 + 120(60) - 2000 = 1600$ 元'
          ],
          answer: '售價 $60$ 元時月利潤最大，為 $1600$ 元'
        },
        hint: '二次函數的最值在頂點處取得'
      },
      c: {
        question: '若要保證月利潤不少於 $1200$ 元，求售價的取值範圍',
        solution: {
          steps: [
            '建立不等式：$-x^2 + 120x - 2000 \\geq 1200$',
            '化簡：$-x^2 + 120x - 3200 \\geq 0$，即 $x^2 - 120x + 3200 \\leq 0$',
            '使用公式法求根：$\\Delta = 120^2 - 4(3200) = 14400 - 12800 = 1600$',
            '$x = \\frac{120 \\pm 40}{2} = 80$ 或 $40$，所以售價範圍為 $[40, 80]$'
          ],
          answer: '售價在 $40$ 元到 $80$ 元之間'
        },
        hint: '解二次不等式確定函數值的範圍'
      }
    }
  },
  {
    id: 'M08_11_02',
    topicId: 'M08_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '拋物線運動建模：',
    parts: {
      a: {
        question: '一球從地面以初速度 $v_0 = 30$ 米/秒垂直向上拋出，建立高度與時間的函數關係（$g = 10$ 米/秒²）',
        solution: {
          steps: [
            '根據運動學公式：$h = v_0 t - \\frac{1}{2}gt^2$',
            '代入已知數據：$v_0 = 30$，$g = 10$',
            '高度函數：$h(t) = 30t - 5t^2$',
            '這是一個開口向下的二次函數'
          ],
          answer: '$h(t) = 30t - 5t^2$ 米'
        },
        hint: '豎直上拋運動的位移公式'
      },
      b: {
        question: '求球達到最高點的時間和最大高度',
        solution: {
          steps: [
            '對高度函數求導：$h\'(t) = 30 - 10t$',
            '令 $h\'(t) = 0$：$30 - 10t = 0$，得 $t = 3$ 秒',
            '最大高度：$h(3) = 30(3) - 5(3)^2 = 90 - 45 = 45$ 米',
            '或用頂點公式：$t = -\\frac{b}{2a} = -\\frac{30}{2(-5)} = 3$ 秒'
          ],
          answer: '最高點時間：$3$ 秒，最大高度：$45$ 米'
        },
        hint: '最值點在導數為零處或用頂點公式'
      },
      c: {
        question: '求球在空中的總時間',
        solution: {
          steps: [
            '球落地時高度為零：$h(t) = 0$',
            '解方程：$30t - 5t^2 = 0$',
            '提取公因數：$5t(6 - t) = 0$',
            '得 $t = 0$（拋出時刻）或 $t = 6$（落地時刻）'
          ],
          answer: '球在空中的總時間為 $6$ 秒'
        },
        hint: '令高度函數等於零求解'
      }
    }
  },
  {
    id: 'M08_11_03',
    topicId: 'M08_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '幾何最值問題：',
    parts: {
      a: {
        question: '用 $60$ 米的籬笆圍成一個矩形菜園，一邊靠牆不需要籬笆。設垂直於牆的邊長為 $x$ 米，建立面積函數',
        solution: {
          steps: [
            '設垂直於牆的邊長為 $x$ 米，平行於牆的邊長為 $y$ 米',
            '籬笆約束：$2x + y = 60$，得 $y = 60 - 2x$',
            '面積函數：$S(x) = xy = x(60 - 2x) = 60x - 2x^2$',
            '定義域：$0 < x < 30$（保證 $y > 0$）'
          ],
          answer: '$S(x) = 60x - 2x^2$，$0 < x < 30$'
        },
        hint: '利用約束條件消除一個變量'
      },
      b: {
        question: '求使面積最大的 $x$ 值和最大面積',
        solution: {
          steps: [
            '面積函數 $S(x) = -2x^2 + 60x$ 是開口向下的拋物線',
            '頂點橫坐標：$x = -\\frac{60}{2(-2)} = 15$ 米',
            '對應的 $y = 60 - 2(15) = 30$ 米',
            '最大面積：$S(15) = 60(15) - 2(15)^2 = 900 - 450 = 450$ 平方米'
          ],
          answer: '$x = 15$ 米時面積最大，最大面積為 $450$ 平方米'
        },
        hint: '二次函數最值在頂點處取得'
      },
      c: {
        question: '若要使面積不少於 $400$ 平方米，求 $x$ 的取值範圍',
        solution: {
          steps: [
            '建立不等式：$60x - 2x^2 \\geq 400$',
            '整理：$2x^2 - 60x + 400 \\leq 0$，即 $x^2 - 30x + 200 \\leq 0$',
            '使用公式法：$\\Delta = 900 - 800 = 100$',
            '$x = \\frac{30 \\pm 10}{2} = 20$ 或 $10$，所以 $10 \\leq x \\leq 20$'
          ],
          answer: '$x$ 的取值範圍為 $[10, 20]$'
        },
        hint: '解二次不等式求函數值的約束範圍'
      }
    }
  },
  {
    id: 'M08_11_04',
    topicId: 'M08_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '成本與收益模型：',
    parts: {
      a: {
        question: '某公司的日產量為 $x$ 件時，總成本為 $C(x) = x^2 + 10x + 100$ 元，每件產品售價為 $50$ 元。建立日利潤函數',
        solution: {
          steps: [
            '總收入：$R(x) = 50x$ 元',
            '總成本：$C(x) = x^2 + 10x + 100$ 元',
            '日利潤 = 總收入 - 總成本',
            '日利潤函數：$P(x) = 50x - (x^2 + 10x + 100) = -x^2 + 40x - 100$'
          ],
          answer: '$P(x) = -x^2 + 40x - 100$'
        },
        hint: '利潤 = 收入 - 成本'
      },
      b: {
        question: '求使日利潤最大的產量和最大日利潤',
        solution: {
          steps: [
            '利潤函數 $P(x) = -x^2 + 40x - 100$ 開口向下',
            '頂點橫坐標：$x = -\\frac{40}{2(-1)} = 20$ 件',
            '最大日利潤：$P(20) = -(20)^2 + 40(20) - 100 = -400 + 800 - 100 = 300$ 元',
            '驗證：$P\'(x) = -2x + 40$，$P\'(20) = 0$ ✓'
          ],
          answer: '日產量 $20$ 件時利潤最大，最大日利潤 $300$ 元'
        },
        hint: '二次函數最值在頂點處'
      },
      c: {
        question: '若要保證不虧損，求產量的取值範圍',
        solution: {
          steps: [
            '不虧損條件：$P(x) \\geq 0$',
            '解不等式：$-x^2 + 40x - 100 \\geq 0$',
            '即：$x^2 - 40x + 100 \\leq 0$',
            '判別式：$\\Delta = 1600 - 400 = 1200$，$x = \\frac{40 \\pm 20\\sqrt{3}}{2} = 20 \\pm 10\\sqrt{3}$',
            '不虧損範圍：$20 - 10\\sqrt{3} \\leq x \\leq 20 + 10\\sqrt{3}$'
          ],
          answer: '產量範圍：$[20 - 10\\sqrt{3}, 20 + 10\\sqrt{3}]$ 件'
        },
        hint: '解二次不等式確定非負區間'
      }
    }
  },
  {
    id: 'M08_11_05',
    topicId: 'M08_11',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '人口增長模型：',
    parts: {
      a: {
        question: '某地區人口增長遵循模型 $P(t) = -0.1t^2 + 2t + 100$（萬人），其中 $t$ 為年份（以某年為起點）。分析該模型的含義',
        solution: {
          steps: [
            '這是一個開口向下的二次函數模型',
            '初始人口（$t = 0$）：$P(0) = 100$ 萬人',
            '二次項係數為負，表示增長率逐漸放緩',
            '該模型預測人口先增長後下降'
          ],
          answer: '初始人口 $100$ 萬，人口增長率逐漸放緩並最終下降'
        },
        hint: '分析二次函數的幾何意義'
      },
      b: {
        question: '求人口達到峰值的時間和峰值人口',
        solution: {
          steps: [
            '人口函數 $P(t) = -0.1t^2 + 2t + 100$ 的頂點即為峰值',
            '頂點時間：$t = -\\frac{2}{2(-0.1)} = 10$ 年',
            '峰值人口：$P(10) = -0.1(10)^2 + 2(10) + 100 = -10 + 20 + 100 = 110$ 萬人',
            '即第 $10$ 年人口達到峰值 $110$ 萬人'
          ],
          answer: '第 $10$ 年達到峰值，峰值人口 $110$ 萬人'
        },
        hint: '二次函數的峰值在頂點處'
      },
      c: {
        question: '預測人口何時回到初始水平',
        solution: {
          steps: [
            '令 $P(t) = 100$：$-0.1t^2 + 2t + 100 = 100$',
            '化簡：$-0.1t^2 + 2t = 0$',
            '提取公因數：$t(-0.1t + 2) = 0$',
            '得 $t = 0$（初始時刻）或 $t = 20$（回到初始水平的時刻）'
          ],
          answer: '第 $20$ 年人口回到初始水平 $100$ 萬人'
        },
        hint: '令函數值等於初始值求解'
      }
    }
  },

  // M08-12: 待定係數
  {
    id: 'M08_12_01',
    topicId: 'M08_12',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '利用待定係數法求二次函數解析式：',
    parts: {
      a: {
        question: '已知二次函數 $f(x) = ax^2 + bx + c$ 過點 $(1, 4)$、$(2, 9)$、$(3, 16)$，求解析式',
        solution: {
          steps: [
            '建立方程組：$\\begin{cases} a + b + c = 4 \\\\ 4a + 2b + c = 9 \\\\ 9a + 3b + c = 16 \\end{cases}$',
            '消元：第二式減第一式得 $3a + b = 5$，第三式減第二式得 $5a + b = 7$',
            '解得：$(5a + b) - (3a + b) = 7 - 5$，即 $2a = 2$，所以 $a = 1$',
            '代入得 $b = 2$，$c = 1$，所以 $f(x) = x^2 + 2x + 1$'
          ],
          answer: '$f(x) = x^2 + 2x + 1$'
        },
        hint: '三個未知數需要三個條件建立方程組'
      },
      b: {
        question: '驗證所求解析式並觀察函數的特點',
        solution: {
          steps: [
            '驗證：$f(1) = 1 + 2 + 1 = 4$ ✓，$f(2) = 4 + 4 + 1 = 9$ ✓，$f(3) = 9 + 6 + 1 = 16$ ✓',
            '觀察：$f(1) = 1^2 + 2(1) + 1 = (1+1)^2 = 4$',
            '$f(2) = 2^2 + 2(2) + 1 = (2+1)^2 = 9$，$f(3) = 3^2 + 2(3) + 1 = (3+1)^2 = 16$',
            '發現：$f(x) = x^2 + 2x + 1 = (x+1)^2$'
          ],
          answer: '$f(x) = (x+1)^2$，函數值為 $(x+1)$ 的平方'
        },
        hint: '驗證後觀察函數的規律性'
      },
      c: {
        question: '求函數的頂點坐標和對稱軸',
        solution: {
          steps: [
            '由 $f(x) = (x+1)^2$ 可知這是頂點式',
            '頂點坐標：$(-1, 0)$',
            '對稱軸：$x = -1$',
            '驗證：$f(-1) = (-1+1)^2 = 0$ ✓'
          ],
          answer: '頂點：$(-1, 0)$，對稱軸：$x = -1$'
        },
        hint: '頂點式 $f(x) = a(x-h)^2 + k$ 的頂點為 $(h, k)$'
      }
    }
  },
  {
    id: 'M08_12_02',
    topicId: 'M08_12',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '根據特殊條件求解析式：',
    parts: {
      a: {
        question: '已知二次函數的頂點為 $(2, -3)$，且過點 $(0, 1)$，求解析式',
        solution: {
          steps: [
            '由頂點 $(2, -3)$ 可設頂點式：$f(x) = a(x-2)^2 - 3$',
            '利用過點 $(0, 1)$ 的條件：$f(0) = 1$',
            '代入：$a(0-2)^2 - 3 = 1$',
            '解得：$4a - 3 = 1$，所以 $a = 1$，$f(x) = (x-2)^2 - 3$'
          ],
          answer: '$f(x) = (x-2)^2 - 3$'
        },
        hint: '已知頂點時用頂點式最方便'
      },
      b: {
        question: '將上述解析式展開為一般式',
        solution: {
          steps: [
            '展開：$f(x) = (x-2)^2 - 3$',
            '$= x^2 - 4x + 4 - 3$',
            '$= x^2 - 4x + 1$',
            '驗證頂點：$x = -\\frac{-4}{2(1)} = 2$，$f(2) = 4 - 8 + 1 = -3$ ✓'
          ],
          answer: '$f(x) = x^2 - 4x + 1$'
        },
        hint: '展開完全平方式'
      },
      c: {
        question: '求函數與 $x$ 軸的交點',
        solution: {
          steps: [
            '令 $f(x) = 0$：$x^2 - 4x + 1 = 0$',
            '使用公式法：$\\Delta = 16 - 4 = 12$',
            '$x = \\frac{4 \\pm \\sqrt{12}}{2} = \\frac{4 \\pm 2\\sqrt{3}}{2} = 2 \\pm \\sqrt{3}$',
            '交點：$(2 - \\sqrt{3}, 0)$ 和 $(2 + \\sqrt{3}, 0)$'
          ],
          answer: '交點：$(2 - \\sqrt{3}, 0)$ 和 $(2 + \\sqrt{3}, 0)$'
        },
        hint: '令函數值為零求解'
      }
    }
  },
  {
    id: 'M08_12_03',
    topicId: 'M08_12',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '根據根的條件求解析式：',
    parts: {
      a: {
        question: '已知二次函數的兩根為 $x_1 = -1$，$x_2 = 3$，且過點 $(0, -6)$，求解析式',
        solution: {
          steps: [
            '由兩根可設因式分解式：$f(x) = a(x+1)(x-3)$',
            '利用過點 $(0, -6)$：$f(0) = -6$',
            '代入：$a(0+1)(0-3) = -6$',
            '解得：$-3a = -6$，所以 $a = 2$，$f(x) = 2(x+1)(x-3)$'
          ],
          answer: '$f(x) = 2(x+1)(x-3)$'
        },
        hint: '已知兩根時用因式分解式'
      },
      b: {
        question: '將解析式展開並求頂點坐標',
        solution: {
          steps: [
            '展開：$f(x) = 2(x+1)(x-3) = 2(x^2 - 2x - 3) = 2x^2 - 4x - 6$',
            '頂點橫坐標：$x = -\\frac{-4}{2(2)} = 1$',
            '頂點縱坐標：$f(1) = 2(1)^2 - 4(1) - 6 = 2 - 4 - 6 = -8$',
            '頂點坐標：$(1, -8)$'
          ],
          answer: '$f(x) = 2x^2 - 4x - 6$，頂點：$(1, -8)$'
        },
        hint: '頂點在兩根中點處'
      },
      c: {
        question: '驗證韋達定理',
        solution: {
          steps: [
            '對於方程 $2x^2 - 4x - 6 = 0$，即 $x^2 - 2x - 3 = 0$',
            '兩根之和：$x_1 + x_2 = -1 + 3 = 2 = -\\frac{-2}{1}$ ✓',
            '兩根之積：$x_1 \\cdot x_2 = (-1) \\times 3 = -3 = \\frac{-3}{1}$ ✓',
            '韋達定理成立'
          ],
          answer: '韋達定理驗證成立'
        },
        hint: '韋達定理：$x_1 + x_2 = -\\frac{b}{a}$，$x_1 x_2 = \\frac{c}{a}$'
      }
    }
  },
  {
    id: 'M08_12_04',
    topicId: 'M08_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '複雜條件下的待定係數：',
    parts: {
      a: {
        question: '已知二次函數 $f(x) = ax^2 + bx + c$ 滿足：$f(0) = 1$，$f(1) = 4$，且 $f(x)$ 的最小值為 $\\frac{3}{4}$，求解析式',
        solution: {
          steps: [
            '由條件：$f(0) = c = 1$，$f(1) = a + b + c = 4$，得 $a + b = 3$',
            '設頂點橫坐標為 $t$，則最小值 $f(t) = \\frac{3}{4}$',
            '頂點橫坐標：$t = -\\frac{b}{2a}$，頂點縱坐標：$f(t) = c - \\frac{b^2}{4a} = 1 - \\frac{b^2}{4a} = \\frac{3}{4}$',
            '得 $\\frac{b^2}{4a} = \\frac{1}{4}$，即 $b^2 = a$，結合 $a + b = 3$ 求解'
          ],
          answer: '需要進一步計算求出具體係數'
        },
        hint: '利用頂點坐標公式和最值條件'
      },
      b: {
        question: '解上述方程組求出 $a$、$b$、$c$ 的值',
        solution: {
          steps: [
            '由 $a + b = 3$ 得 $a = 3 - b$',
            '代入 $b^2 = a$：$b^2 = 3 - b$',
            '整理：$b^2 + b - 3 = 0$',
            '解得：$b = \\frac{-1 \\pm \\sqrt{1 + 12}}{2} = \\frac{-1 \\pm \\sqrt{13}}{2}$',
            '由於需要最小值，$a > 0$，取 $b = \\frac{-1 + \\sqrt{13}}{2}$，$a = \\frac{7 - \\sqrt{13}}{2}$'
          ],
          answer: '$a = \\frac{7 - \\sqrt{13}}{2}$，$b = \\frac{-1 + \\sqrt{13}}{2}$，$c = 1$'
        },
        hint: '注意 $a > 0$ 的條件（有最小值）'
      },
      c: {
        question: '驗證所求解析式的正確性',
        solution: {
          steps: [
            '驗證 $f(0) = c = 1$ ✓',
            '驗證 $f(1) = a + b + c = \\frac{7-\\sqrt{13}}{2} + \\frac{-1+\\sqrt{13}}{2} + 1 = 3 + 1 = 4$ ✓',
            '頂點橫坐標：$t = -\\frac{b}{2a}$，最小值確實為 $\\frac{3}{4}$',
            '所有條件均滿足'
          ],
          answer: '解析式正確'
        },
        hint: '逐一驗證所有給定條件'
      }
    }
  },
  {
    id: 'M08_12_05',
    topicId: 'M08_12',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '對稱性條件下的待定係數：',
    parts: {
      a: {
        question: '已知二次函數關於直線 $x = 1$ 對稱，過點 $(0, 3)$ 和 $(3, 12)$，求解析式',
        solution: {
          steps: [
            '由對稱軸 $x = 1$ 可設：$f(x) = a(x-1)^2 + k$',
            '利用過點 $(0, 3)$：$f(0) = a(0-1)^2 + k = a + k = 3$',
            '利用過點 $(3, 12)$：$f(3) = a(3-1)^2 + k = 4a + k = 12$',
            '解方程組：$4a + k - (a + k) = 12 - 3$，得 $3a = 9$，所以 $a = 3$，$k = 0$'
          ],
          answer: '$f(x) = 3(x-1)^2 = 3x^2 - 6x + 3$'
        },
        hint: '已知對稱軸時用頂點式設函數'
      },
      b: {
        question: '求函數的頂點和開口方向',
        solution: {
          steps: [
            '由 $f(x) = 3(x-1)^2$ 可知：',
            '頂點坐標：$(1, 0)$',
            '開口方向：$a = 3 > 0$，開口向上',
            '這是最簡單的頂點式形式'
          ],
          answer: '頂點：$(1, 0)$，開口向上'
        },
        hint: '頂點式直接給出頂點信息'
      },
      c: {
        question: '利用對稱性求 $f(2)$ 和 $f(-1)$ 的值',
        solution: {
          steps: [
            '由對稱軸 $x = 1$ 知：$f(2) = f(0) = 3$（$2$ 和 $0$ 關於 $x = 1$ 對稱）',
            '同理：$f(-1) = f(3) = 12$（$-1$ 和 $3$ 關於 $x = 1$ 對稱）',
            '驗證：$f(2) = 3(2-1)^2 = 3$ ✓',
            '$f(-1) = 3(-1-1)^2 = 3 \\times 4 = 12$ ✓'
          ],
          answer: '$f(2) = 3$，$f(-1) = 12$'
        },
        hint: '利用對稱性可以快速求值'
      }
    }
  }
];

// 導出模組
export default {
  topics: M08_TOPICS_11_TO_20,
  problems: M08_PROBLEMS_11_TO_20
}; 